import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import CryptoJS from 'crypto-js';
import { useTranslation } from 'react-i18next';

// Shared
import Configs from '@/Configs';
import { persistLoginBrandCache } from '@/authentication/core/loginBrandCache.js';
import { queryKeysInvalidate } from '@/queries/queries-keys';
import { createCompanyColorCssVariable } from '@/utils/companyColor';
import {
  fetchBrandImagePublicUrl,
  isSafeDirectImagePreviewUrl,
  shouldResolveBrandImageViaPublicUrl,
} from '@/views/settings/company/core/resolveBrandImagePreviewUrl';

// Relative imports
import { useAuth } from './appContext';
import * as apiCalls from './apiCalls';
import { handleApiError } from './utils/handleAPIerrors';

/** @type {React.Context<import('./dataContext').DataContextValues>} */
/** Exported for Storybook / tests that provide a mock data value without `DataProvider`. */
export const DataContext = createContext(/** @type {any} */({}));

// ------------------------------------------------------------------------------------------------
export const DataProvider = ({ children }) => {
  // 3P Translation
  const { t } = useTranslation();

  // Auth context
  const { correlationId, token, companyID, employeeID, securityLevel, setIsAuthenticationLoading } =
    useAuth();

  // Company personalization
  const [logoPublicLargeURL, setLogoPublicLargeURL] = useState(Configs.companyLogoLarge);
  const [logoPublicSmallURL, setLogoPublicSmallURL] = useState(Configs.companyLogoSmall);

  // Messaging toaster - support for multiple toasts
  const [toasterMessages, setToasterMessages] = useState([]);
  const [toasterMessage, setToasterMessage] = useState(null); // Keep for backward compatibility

  // Navbar collapse. Missing preference and the stored string "false" both mean expanded.
  const navBarCollapsedAux = useMemo(() => {
    return localStorage.getItem('navBarCollapsed') === 'true';
  }, []);
  const [navBarCollapsed, setNavBarCollapsed] = useState(navBarCollapsedAux);

  // Feedback popup
  const [showFeedbackPopup, setShowFeedbackPopup] = useState(false);

  // Employee Profile — prefer full `employeeProfile` (includes careerInfo); fall back to employeeInfo.
  const profileAux = useMemo(() => {
    try {
      const fullStored = sessionStorage.getItem('employeeProfile');
      if (fullStored) {
        const full = JSON.parse(
          CryptoJS.AES.decrypt(fullStored, Configs.privateKey).toString(CryptoJS.enc.Utf8)
        );
        if (full && typeof full === 'object') {
          return full;
        }
      }
    } catch (error) {
      console.error('[dataContext.jsx] Failed to parse employeeProfile from sessionStorage', error);
    }

    try {
      const storedData = sessionStorage.getItem('employeeInfo');
      if (!storedData) {
        return null;
      }
      const employeeInfo = JSON.parse(
        CryptoJS.AES.decrypt(storedData, Configs.privateKey).toString(CryptoJS.enc.Utf8)
      );
      // Legacy sessions stored employeeInfo only — wrap so Self can use employeeInfo.* paths.
      return employeeInfo?.employeeID != null && !employeeInfo?.employeeInfo
        ? { employeeInfo }
        : employeeInfo;
    } catch (error) {
      console.error('[dataContext.jsx] Failed to parse employeeInfo from sessionStorage', error);
      return null;
    }
  }, []);
  /** @type {[import('./dataContext').ContextProfile | null, React.Dispatch<React.SetStateAction<import('./dataContext').ContextProfile | null>>]} */
  const [profile, setProfile] = useState(profileAux);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [errorLoadingProfile, setErrorLoadingProfile] = useState(null);

  // Company Preferences
  const companyPreferencesAux = useMemo(() => {
    const storedData = sessionStorage.getItem('companyPreferences');
    return storedData
      ? JSON.parse(CryptoJS.AES.decrypt(storedData, Configs.privateKey).toString(CryptoJS.enc.Utf8))
      : null;
  }, []);
  /** @type {[import('./dataContext').CompanyPreferences | null, React.Dispatch<React.SetStateAction<import('./dataContext').CompanyPreferences | null>>]} */
  const [companyPreferences, setCompanyPreferences] = useState(companyPreferencesAux);

  // Company Background Image URL
  const [companyBackgroundImageURL, setCompanyBackgroundImageURL] = useState(null);

  // Team data
  const teamDirectAux = useMemo(() => {
    const storedData = sessionStorage.getItem('teamDirects');
    return storedData
      ? JSON.parse(CryptoJS.AES.decrypt(storedData, Configs.privateKey).toString(CryptoJS.enc.Utf8))
      : null;
  }, []);
  const teamOrgAux = useMemo(() => {
    const storedData = sessionStorage.getItem('teamOrg');
    return storedData
      ? JSON.parse(CryptoJS.AES.decrypt(storedData, Configs.privateKey).toString(CryptoJS.enc.Utf8))
      : null;
  }, []);
  /** @type {[import('./dataContext').EmployeeInfo[] | null, React.Dispatch<React.SetStateAction<import('./dataContext').EmployeeInfo[] | null>>]} */
  const [teamDirects, setTeamDirects] = useState(teamDirectAux);
  const [isDirectsLoading, setIsDirectsLoading] = useState(false);
  /** @type {[import('./dataContext').EmployeeInfo[] | null, React.Dispatch<React.SetStateAction<import('./dataContext').EmployeeInfo[] | null>>]} */
  const [teamOrg, setTeamOrg] = useState(teamOrgAux);
  const [isOrgLoading, setIsOrgLoading] = useState(false);

  // Pending to-dos
  const [pendingToDos, setPendingToDos] = useState(null);
  const [isPendingToDosLoading, setIsPendingToDosLoading] = useState(false);
  const [keyKPIs, setKeyKPIs] = useState(null);
  const [isKeyKPIsLoading, setIsKeyKPIsLoading] = useState(false);

  // Selected profile - for context switching
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [isSelectedProfileLoading, setIsSelectedProfileLoading] = useState(false);

  // PERFORM
  // Performance - Personal data for widget
  const [personalPerformanceData, setPersonalPerformanceData] = useState(null);
  const [isPersonalPerformanceLoading, setIsPersonalPerformanceLoading] = useState(false);

  // Team review (draft GETs live in TanStack Query — see useGetPerformance*Draft)
  const [performanceReviewCampaigns, setPerformanceReviewCampaigns] = useState(null);
  const [livePerformanceCampaign, setLivePerformanceCampaign] = useState(false);
  const [isPerformanceLiveCampaignsLoading, setIsPerformanceLiveCampaignsLoading] = useState(false);
  const [performanceFeedbackProviders, setPerformanceFeedbackProviders] = useState(null);
  const [isPerformanceFeedbackProvidersLoading, setIsPerformanceFeedbackProvidersLoading] =
    useState(false);

  const [performanceReviewHistory, setPerformanceReviewHistory] = useState(null);
  const [isPerformanceReviewHistoryLoading, setIsPerformanceReviewHistoryLoading] = useState(false);

  // Calibration
  const [calibrationCampaign, setCalibrationCampaign] = useState(null);
  const [isCalibrationCampaignLoading, setIsCalibrationCampaignLoading] = useState(false);
  const [liveCalibrationCampaign, setLiveCalibrationCampaign] = useState(false);

  // ENGAGEMENT
  // Pulse
  const [pulseReportingData, setPulseReportingData] = useState(null);
  const [isPulseReportingDataLoading, setIsPulseReportingDataLoading] = useState(false);

  /** @type {[import('./dataContext').PulseKPI[] | null, React.Dispatch<React.SetStateAction<import('./dataContext').PulseKPI[] | null>>]} */
  const [pulseKeyIndicators, setPulseKeyIndicators] = useState(null);
  const [isPulseKeyIndicatorsLoading, setIsPulseKeyIndicatorsLoading] = useState(false);

  const [pulseHomeGraphFrontendData, setPulseHomeGraphFrontendData] = useState(null);
  const [isPulseHomeGraphFrontendDataLoading, setIsPulseHomeGraphFrontendDataLoading] =
    useState(false);

  const [allPulseQuestions, setAllPulseQuestions] = useState(null);
  const [isAllPulseQuestionsLoading, setIsAllPulseQuestionsLoading] = useState(false);

  // Learning
  const [learningCoursesCatalogue, setLearningCoursesCatalogue] = useState(null);
  const [isLearningCoursesCatalogueLoading, setIsLearningCoursesCatalogueLoading] = useState(false);

  // Career guideline state removed — careerGuidelineCreate resolves from URL params +
  // useGetCareerJobGuidelines list; no global fast-path needed.

  // ESSENTIALS
  // Holidays
  const [holidaysTeamPendingRequests, setHolidaysTeamPendingRequests] = useState(null);
  const [isHolidaysTeamPendingRequestsLoading, setIsHolidaysTeamPendingRequestsLoading] =
    useState(false);

  const [selfHolidays, setSelfHolidays] = useState(null);
  const [isSelfHolidaysLoading, setIsSelfHolidaysLoading] = useState(false);

  // SUPER ADMIN
  const [selectedCompany, setSelectedCompany] = useState(null);
  // Company
  const [CCOaaSReport, setCCOaaSReport] = useState(null);
  const [isCCOaaSReportLoading, setIsCCOaaSReportLoading] = useState(false);

  const [CCOaaScompanyDetailsAIAnalysis, setCCOaaSCompanyDetailsAIAnalysis] = useState(null);
  const [isCCOaaSAICompanyDetailsAnalysisLoading, setIsCCOaaSAICompanyDetailsAnalysisLoading] =
    useState(true);

  // ---------------------------------------------------------------------------------------------------
  // Toaster methods
  // ---------------------------------------------------------------------------------------------------
  const closeToaster = () => {
    setToasterMessage(null);
  };

  // Enhanced toaster methods for multiple toasts
  const addToast = useCallback((message) => {
    const id = Date.now() + Math.random();
    const toast = {
      id,
      message: message.message,
      type: message.type,
      persistent: message.persistent || false,
      action: message.action || null,
      timestamp: Date.now()
    };
    
    // Use functional state update to ensure we always get the latest state
    setToasterMessages(prev => {
      const updated = [...prev, toast];
      return updated;
    });
    
    // Also set the single toaster message for backward compatibility (only if no existing message)
    setToasterMessage(prev => {
      // Only set if there's no current message, or if we're explicitly replacing it
      if (!prev) {
        return message;
      }
      return prev;
    });
    
    return id;
  }, []); // Remove toasterMessage from dependencies to avoid stale closures

  const removeToast = useCallback((id) => {
    setToasterMessages(prev => {
      const updated = prev.filter(toast => toast.id !== id);
      // Clear single toaster if it's the last one
      if (updated.length === 0) {
        setToasterMessage(null);
      }
      return updated;
    });
  }, []);

  const clearAllToasts = useCallback(() => {
    setToasterMessages([]);
    setToasterMessage(null);
  }, []);

  // ------------------------------------------------
  // Navbar method
  // ------------------------------------------------
  const setCollapseNavBar = useCallback((collapsed) => {
    const isCollapsed = collapsed === true;
    setNavBarCollapsed(isCollapsed);
    localStorage.setItem('navBarCollapsed', isCollapsed ? 'true' : 'false');
  }, []);

  // ------------------------------------------------
  // API calls
  // ------------------------------------------------
  const getPublicLogoLargeURL = useCallback(async () => {
    const reference = companyPreferences?.personalization?.logoLarge;
    const refStr = reference != null ? String(reference).trim() : '';
    if (!refStr || !companyID || !employeeID || !token) {
      return;
    }

    try {
      if (shouldResolveBrandImageViaPublicUrl(refStr)) {
        const publicUrl = await fetchBrandImagePublicUrl(
          companyID,
          employeeID,
          token,
          refStr,
          correlationId
        );
        if (Configs.devEnvironment) {
          console.log('[dataContext.js][getPublicLogoLargeURL()] publicUrl: ', publicUrl);
        }
        if (publicUrl) {
          setLogoPublicLargeURL(publicUrl);
          persistLoginBrandCache(companyID, companyPreferences, publicUrl);
        }
        return;
      }

      if (isSafeDirectImagePreviewUrl(refStr)) {
        setLogoPublicLargeURL(refStr);
        persistLoginBrandCache(companyID, companyPreferences, refStr);
      }
    } catch (error) {
      console.error('[dataContext.js][getPublicLogoLargeURL()] Error fetching data: ', error);
    }
  }, [companyID, employeeID, token, companyPreferences, correlationId]);

  const getPublicLogoSmallURL = useCallback(async () => {
    const reference = companyPreferences?.personalization?.logoSmall;
    const refStr = reference != null ? String(reference).trim() : '';
    if (!refStr || !companyID || !employeeID || !token) {
      return;
    }

    try {
      if (shouldResolveBrandImageViaPublicUrl(refStr)) {
        const publicUrl = await fetchBrandImagePublicUrl(
          companyID,
          employeeID,
          token,
          refStr,
          correlationId
        );
        if (Configs.devEnvironment) {
          console.log('[dataContext.js][getPublicLogoSmallURL()] publicUrl: ', publicUrl);
        }
        if (publicUrl) {
          setLogoPublicSmallURL(publicUrl);
        }
        return;
      }

      if (isSafeDirectImagePreviewUrl(refStr)) {
        setLogoPublicSmallURL(refStr);
      }
    } catch (error) {
      console.error('[dataContext.js][getPublicLogoSmallURL()] Error fetching data: ', error);
    }
  }, [companyID, employeeID, token, companyPreferences, correlationId]);

  const getPublicBackgroundImageURL = useCallback(async () => {
    try {
      const response = await apiCalls.getPublicURLForFile(
        companyID,
        employeeID,
        token,
        companyPreferences?.personalization?.backgroundImageURL,
        correlationId
      );
      if (Configs.devEnvironment)
        console.log('[dataContext.js][getPublicBackgroundImageURL()] response: ', response);
      setCompanyBackgroundImageURL(response?.url);
    } catch (error) {
      console.error('[dataContext.js][getPublicBackgroundImageURL()] Error fetching data: ', error);
    }
  }, [companyID, employeeID, token, companyPreferences, correlationId]);

  // ------------------------------------------------
  async function getProfile(
    employeeIDAux,
    companyIDAux,
    bearerToken,
    csrfToken,
    correlationId = null
  ) {
    // Set Loading status
    if (selectedProfile) setIsSelectedProfileLoading(true);
    if (!selectedProfile) setIsProfileLoading(true);

    if (Configs.devEnvironment)
      console.log(
        '[dataContext.js][getProfile()] request: ',
        employeeIDAux,
        companyIDAux,
        token ? token : bearerToken
      );

    try {
      // fetch from server
      const response = await apiCalls.getEmployeeProfile(
        companyIDAux ? companyIDAux : companyID,
        employeeIDAux ? employeeIDAux : employeeID,
        bearerToken ? bearerToken : token,
        correlationId
      );

      setIsProfileLoading(false);
      setIsSelectedProfileLoading(false);

      // Clean up loading variable on Auth
      setIsAuthenticationLoading(false);

      const error = handleApiError(response, t);

      if (error.isError) {
        setToasterMessage({ message: error.errorMessage, type: Configs.errorToaster });
        console.error('[dataContext.js][getProfile()] Error fetching data: ', error.errorMessage);
        return;
      }

      if (Configs.devEnvironment)
        console.log('[dataContext.js][getProfile()] Get Employee Info: ', response);
      if (response) {
        // Coerce IDs — auth employeeID may be string while employeeInfo.employeeID is number.
        const responseEmployeeID = response?.employeeInfo?.employeeID;
        const isSelfProfile =
          responseEmployeeID != null &&
          employeeID != null &&
          String(responseEmployeeID) === String(employeeID);

        if (isSelfProfile) {
          // sessionStorage.setItem("roles", CryptoJS.AES.encrypt(JSON.stringify(body.roles), Configs.privateKey).toString());
          sessionStorage.setItem(
            'employeeInfo',
            CryptoJS.AES.encrypt(
              JSON.stringify(response.employeeInfo),
              Configs.privateKey
            ).toString()
          );
          sessionStorage.setItem(
            'employeeProfile',
            CryptoJS.AES.encrypt(JSON.stringify(response), Configs.privateKey).toString()
          );
          if (response?.managerInfo)
            sessionStorage.setItem(
              'managerInfo',
              CryptoJS.AES.encrypt(
                JSON.stringify(response.managerInfo),
                Configs.privateKey
              ).toString()
            );
          sessionStorage.setItem(
            'companyInfo',
            CryptoJS.AES.encrypt(
              JSON.stringify(response.companyInfo),
              Configs.privateKey
            ).toString()
          );
          sessionStorage.setItem(
            'companyPreferences',
            CryptoJS.AES.encrypt(
              JSON.stringify(response.companyPreferences),
              Configs.privateKey
            ).toString()
          );
          // sessionStorage.setItem("activeModules", CryptoJS.AES.encrypt(JSON.stringify(response.companyPreferences.activeModules), Configs.privateKey).toString());
          sessionStorage.setItem(
            'securityLevel',
            CryptoJS.AES.encrypt(
              JSON.stringify(response.roles.securityLevels),
              Configs.privateKey
            ).toString()
          );

          // Update state — full profile includes careerInfo for Career Self next-role.
          setProfile(response);
          // setSecurityLevel(response.roles.securityLevels); // REVIEW IF REMOVING THIS BROKE ANYTHING
          setCompanyPreferences(response?.companyPreferences);
          persistLoginBrandCache(companyID, response?.companyPreferences);
          setCompanyBackgroundImageURL(
            response?.companyPreferences?.personalization?.backgroundImageURL
          );
        } else {
          // Different subject — always replace so post-write refreshes (e.g. careerInfo) stick
          setSelectedProfile(response);
        }
      }
    } catch (error) {
      console.error('[dataContext.js][getProfile()] Error fetching data: ', error);
      setIsProfileLoading(false);
      setIsSelectedProfileLoading(false);
      // Clean up loading variable on Auth
      setIsAuthenticationLoading(false);
    }
  }

  // Team
  // Get team members, only directs
  async function getMyDirectTeam() {
    setIsDirectsLoading(true);

    try {
      const response = await apiCalls.getEmployeesOfMgr(
        companyID,
        employeeID,
        token,
        correlationId
      );

      setIsDirectsLoading(false);

      const error = handleApiError(response, t);

      if (error.isError) {
        setToasterMessage({ message: error.errorMessage, type: Configs.errorToaster });
        console.error(
          '[dataContext.js][getMyDirectTeam()] Error fetching data: ',
          error.errorMessage
        );
        setTeamDirects([]);
        return;
      }

      if (response.length > 0) {
        if (Configs.devEnvironment)
          console.log('[dataContext.js][getMyDirectTeam()] Directs: ', response);
        try {
          sessionStorage.setItem(
            'teamDirects',
            CryptoJS.AES.encrypt(JSON.stringify(response), Configs.privateKey).toString()
          );
        } catch (e) {
          console.error('[dataContext.jsx] error setting item in sessionStorage', e);
        }
        setTeamDirects(response);
        return;
      }

      setTeamDirects([]);
      return;
    } catch (error) {
      console.error('[dataContext.js][getMyDirectTeam()] Error fetching data: ', error);
      setTeamDirects([]);
      setIsDirectsLoading(false);
    }
  }

  async function getMyTeamOrg() {
    setIsOrgLoading(true);

    // fetch from server
    try {
      const response = await apiCalls.getOrgOfMgr(companyID, employeeID, token, correlationId);
      setIsOrgLoading(false);

      const error = handleApiError(response, t);

      if (error.isError) {
        setToasterMessage({ message: error.errorMessage, type: Configs.errorToaster });
        console.error('[dataContext.js][getMyTeamOrg()] Error fetching data: ', error.errorMessage);
        setTeamOrg([]);
        return;
      }

      if (Configs.devEnvironment) console.log('[dataContext.js][getMyTeamOrg()] Org: ', response);

      if (response.length > 0) {
        try {
          sessionStorage.setItem(
            'teamOrg',
            CryptoJS.AES.encrypt(
              JSON.stringify(response[0]?.allSubordinates),
              Configs.privateKey
            ).toString()
          );
        } catch (e) {
          console.error('[dataContext.jsx] error setting item in sessionStorage', e);
        }
        setTeamOrg(response[0]?.allSubordinates);
        return;
      }

      setTeamOrg([]);
      return;
    } catch (error) {
      console.error('[dataContext.js][getMyTeamOrg()] Error fetching data: ', error);
      setTeamOrg([]);
      setIsOrgLoading(false);
    }
  }

  // ------------------------------------------------
  // HOME
  // ------------------------------------------------
  async function getPendingToDos() {
    // Mark as loading
    setIsPendingToDosLoading(true);

    try {
      let response = await apiCalls.getPendingToDos(companyID, employeeID, token, correlationId);

      setIsPendingToDosLoading(false);

      const error = handleApiError(response, t);
      if (error.isError) {
        setToasterMessage({ message: error.errorMessage, type: Configs.errorToaster });
        console.error(
          '[dataContext.js][getPendingToDos()] Error fetching data: ',
          error.errorMessage
        );
        setPendingToDos([]);
        return;
      }

      if (Configs.devEnvironment)
        console.log('[dataContext.js][getPendingToDos()] response: ', response);
      if (response.length > 0) {
        setPendingToDos(response);
        return;
      }

      setPendingToDos([]);
      return;
    } catch (error) {
      setToasterMessage({ message: t('error.server-unreachable'), type: Configs.errorToaster });
      console.error('[Home.js][getPendingToDos()] Error fetching data: ', error);
      setIsPendingToDosLoading(false);
      setPendingToDos([]);
    }
  }

  async function getKeyKPIs() {
    // Mark as loading
    setIsKeyKPIsLoading(true);

    try {
      let response = await apiCalls.getKeyKPIs(companyID, employeeID, token, correlationId);

      setIsKeyKPIsLoading(false);

      const error = handleApiError(response, t);

      if (error.isError) {
        setToasterMessage({ message: error.errorMessage, type: Configs.errorToaster });
        console.error('[dataContext.js][getKeyKPIs()] Error fetching data: ', error.errorMessage);
        setKeyKPIs([]);
        return;
      }

      if (Configs.devEnvironment)
        console.log('[dataContext.js][getKeyKPIs()] response: ', response);
      if (response?.length > 0) {
        setKeyKPIs([...response]);
        return;
      }

      setKeyKPIs([]);
      return;
    } catch (error) {
      setToasterMessage({ message: t('error.server-unreachable'), type: Configs.errorToaster });
      console.error('[Home.js][getKeyKPIs()] Error fetching data: ', error);
      setKeyKPIs([]);
      setIsKeyKPIsLoading(false);
    }
  }

  // ------------------------------------------------
  // TIME OFF / DAYS OFF / HOLIDAYS
  // ------------------------------------------------
  async function getHolidaysTeamPendingRequests() {
    try {
      // Set loading status
      setIsHolidaysTeamPendingRequestsLoading(true);

      const response = await apiCalls.getHolidaysTeamPendingRequests(
        companyID,
        employeeID,
        token,
        correlationId
      );
      if (Configs.devEnvironment)
        console.log('[dataContext.js][getHolidaysTeamPendingRequests()] response: ', response);

      // Review response status
      if (response === 500) {
        setToasterMessage({ message: t('error.server-unreachable'), type: Configs.errorToaster });
      } else if (response === 404) {
        setToasterMessage({ message: t('error.not-found'), type: Configs.errorToaster });
      } else if (response === 403) {
        setToasterMessage({ message: t('error.unauthorized'), type: Configs.errorToaster });
      } else if (response === 401) {
        setToasterMessage({ message: t('error.unauthenticated'), type: Configs.errorToaster });
        sessionStorage.clear();
      } else if (response === 400) {
        setToasterMessage({ message: t('error.bad-request'), type: Configs.errorToaster });
      } else {
        if (Configs.devEnvironment)
          console.log('[dataContext.js][getHolidaysTeamPendingRequests()] response: ', response);
        if (Array.isArray(response)) {
          setHolidaysTeamPendingRequests(response);
        }
      }

      // Resolve loading status
      setIsHolidaysTeamPendingRequestsLoading(false);
    } catch (error) {
      console.error(
        '[daysOffTeam.js][getPendingRequestsForManager()] Error fetching data: ',
        error
      );
      setToasterMessage({ message: t('error.server-unreachable'), type: Configs.errorToaster });
      setIsHolidaysTeamPendingRequestsLoading(false);
    }
  }

  async function getSelfHolidays() {
    try {
      // Set loading status
      setIsSelfHolidaysLoading(true);

      const response = await apiCalls.getHolidaysForEmployee(
        companyID,
        employeeID,
        token,
        correlationId
      );
      if (Configs.devEnvironment)
        console.log('[dataContext.js][getSelfHolidays()] response: ', response);

      // Resolve loading status
      setIsSelfHolidaysLoading(false);

      const error = handleApiError(response, t);

      if (error.isError) {
        setToasterMessage({ message: error.errorMessage, type: Configs.errorToaster });
        console.error(
          '[dataContext.js][getSelfHolidays()] Error fetching data: ',
          error.errorMessage
        );
        setSelfHolidays([]);
        return;
      }

      // Review response status

      if (Configs.devEnvironment)
        console.log('[dataContext.js][getSelfHolidays()] response: ', response);
      if (response || response.length > 0) {
        setSelfHolidays(response);
        return;
      }

      setSelfHolidays([]);
      return;
    } catch (error) {
      console.error('[dataContext.js][getSelfHolidays()] Error fetching data: ', error);
      setToasterMessage({ message: t('error.server-unreachable'), type: Configs.errorToaster });
      setSelfHolidays([]);
      setIsSelfHolidaysLoading(false);
    }
  }

  // ------------------------------------------------
  // PERFORMANCE
  // ------------------------------------------------
  async function getPersonalPerformanceData() {
    try {
      // Set loading status
      setIsPersonalPerformanceLoading(true);

      const response = await apiCalls.getPersonalPerformanceData(
        selectedProfile ? selectedProfile?.companyID : companyID,
        selectedProfile ? selectedProfile?.employeeID : employeeID,
        token,
        correlationId
      );
      if (Configs.devEnvironment)
        console.log('[dataContext.js][getPersonalPerformanceData()] response: ', response);

      // Resolve loading status
      setIsPersonalPerformanceLoading(false);

      const error = handleApiError(response, t);

      if (error.isError) {
        setToasterMessage({ message: error.errorMessage, type: Configs.errorToaster });
        console.error(
          '[dataContext.js][getPersonalPerformanceData()] Error fetching data: ',
          error.errorMessage
        );
        return;
      }

      // Review response status

      if (Configs.devEnvironment)
        console.log('[dataContext.js][getPersonalPerformanceData()] response: ', response);
      if (response.length > 0) {
        transformPersonalPerformanceData(response[0]);
        return;
      }

      setPersonalPerformanceData([]);
      return;
    } catch (error) {
      console.error('[dataContext.js][getPersonalPerformanceData()] Error fetching data: ', error);
      setToasterMessage({ message: t('error.server-unreachable'), type: Configs.errorToaster });
      setPersonalPerformanceData([]);
      setIsPersonalPerformanceLoading(false);
    }
  }

  function transformPersonalPerformanceData(performanceDataAux) {
    // Iterate through
    let transformedData = {};

    performanceDataAux?.values?.forEach((value) => {
      var valueTrimmed = value?.value.replace(/\s/g, '');

      transformedData[valueTrimmed] = parseFloat(value?.selected);
    });

    if (Configs.devEnvironment)
      console.log(
        '[dataContext.js][transformPersonalPerformanceData()] transformedData: ',
        transformedData
      );

    setPersonalPerformanceData(transformedData);
  }

  async function getLivePerformanceCampaigns() {
    try {
      // Set loading status
      setIsPerformanceLiveCampaignsLoading(true);

      const response = await apiCalls.getLivePerformanceCampaigns(
        selectedProfile ? selectedProfile?.companyID : companyID,
        selectedProfile ? selectedProfile?.employeeID : employeeID,
        token,
        correlationId
      );

      if (Configs.devEnvironment)
        console.log('[dataContext.js][getLivePerformanceCampaigns()] response: ', response);

      // Resolve loading status
      setIsPerformanceLiveCampaignsLoading(false);

      // Review response status
      if (response === 500) {
        setToasterMessage({ message: t('error.server-unreachable'), type: Configs.errorToaster });
      } else if (response === 404) {
        setToasterMessage({ message: t('error.not-found'), type: Configs.errorToaster });
      } else if (response === 403) {
        setToasterMessage({ message: t('error.unauthorized'), type: Configs.errorToaster });
      } else if (response === 401) {
        setToasterMessage({ message: t('error.unauthenticated'), type: Configs.errorToaster });
        sessionStorage.clear();
      } else if (response === 400) {
        setToasterMessage({ message: t('error.bad-request'), type: Configs.errorToaster });
      } else if (response === 204) {
        setLivePerformanceCampaign(false);
        setPerformanceReviewCampaigns(null);
        setPerformanceFeedbackProviders(null);
      } else {
        // 200 OK
        if (Configs.devEnvironment)
          console.log('[dataContext.js][getLivePerformanceCampaigns()] response: ', response);
        if (response.length > 0) {
          setLivePerformanceCampaign(true);
          setPerformanceReviewCampaigns(response);
          if (response[0]?._id) {
            getPerformanceFeedbackProviders(response[0]?._id);
          }
        } else {
          setLivePerformanceCampaign(false);
          setPerformanceReviewCampaigns(null);
        }
      }
    } catch (error) {
      console.error(
        '[performanceHome.js][getPerformanceLiveCampaigns()] Error fetching data: ',
        error
      );
      setToasterMessage({ message: t('error.server-unreachable'), type: Configs.errorToaster });
      setIsPerformanceLiveCampaignsLoading(false);
    }
  }

  async function getPerformanceReviewHistory() {
    try {
      // Set loading status
      setIsPerformanceReviewHistoryLoading(true);

      const response = await apiCalls.getPerformanceReviewHistory(
        selectedProfile ? selectedProfile?.companyID : companyID,
        selectedProfile ? selectedProfile?.employeeID : employeeID,
        token,
        correlationId
      );
      if (Configs.devEnvironment)
        console.log('[dataContext.js][getPerformanceReviewHistory()] response: ', response);

      // Resolve loading status
      setIsPerformanceReviewHistoryLoading(false);

      const error = handleApiError(response, t);

      if (error.isError) {
        setToasterMessage({ message: error.errorMessage, type: Configs.errorToaster });
        console.error(
          '[dataContext.js][getPerformanceReviewHistory()] Error fetching data: ',
          error.errorMessage
        );
        setPerformanceReviewHistory([]);
        return;
      }

      // Review response status

      // 200 OK
      if (Configs.devEnvironment)
        console.log('[dataContext.js][getPerformanceReviewHistory()] response: ', response);
      if (response.length > 0) {
        setPerformanceReviewHistory(response);
        return;
      }

      setPerformanceReviewHistory([]);
      return;
    } catch (error) {
      console.error(
        '[performanceHome.js][getPerformanceReviewHistory()] Error fetching data: ',
        error
      );
      setToasterMessage({ message: t('error.server-unreachable'), type: Configs.errorToaster });
      setPerformanceReviewHistory([]);
      setIsPerformanceReviewHistoryLoading(false);
    }
  }

  async function getPerformanceFeedbackProviders(campaignID) {
    try {
      // Set loading status
      setIsPerformanceFeedbackProvidersLoading(true);

      const response = await apiCalls.getPerformanceFeedbackProviders(
        selectedProfile ? selectedProfile?.companyID : companyID,
        selectedProfile ? selectedProfile?.employeeID : employeeID,
        token,
        campaignID,
        correlationId
      );

      if (Configs.devEnvironment)
        console.log('[dataContext.js][getFeedbackProviders()] response: ', response);

      // Resolve loading status
      setIsPerformanceFeedbackProvidersLoading(false);

      const error = handleApiError(response, t);

      if (error.isError) {
        setToasterMessage({ message: error.errorMessage, type: Configs.errorToaster });
        console.error(
          '[dataContext.js][getFeedbackProviders()] Error fetching data: ',
          error.errorMessage
        );
        setPerformanceFeedbackProviders([]);
        return;
      }

      // 200 OK
      if (Configs.devEnvironment)
        console.log('[dataContext.js][getFeedbackProviders()] response: ', response);
      if (response[0]?.feedbackProviders?.length > 0) {
        setPerformanceFeedbackProviders(response[0]?.feedbackProviders);
        return;
      }

      setPerformanceFeedbackProviders([]);
      return;
    } catch (error) {
      console.error('[performanceHome.js][getFeedbackProviders()] Error fetching data: ', error);
      setToasterMessage({ message: t('error.server-unreachable'), type: Configs.errorToaster });
      setIsPerformanceFeedbackProvidersLoading(false);
    }
  }

  // ------------------------------------------------
  // Calibration
  // ------------------------------------------------
  async function getCalibrationData() {
    try {
      // Set loading status
      setIsCalibrationCampaignLoading(true);

      const response = await apiCalls.getCalibrationData(
        companyID,
        employeeID,
        token,
        correlationId
      );
      if (Configs.devEnvironment)
        console.log('[dataContext.js][getCalibrationData()] response: ', response);

      // Resolve loading status
      setIsCalibrationCampaignLoading(false);

      // Review response status
      const error = handleApiError(response, t);

      if (error.isError) {
        setToasterMessage({ message: error.errorMessage, type: Configs.errorToaster });
        console.error(
          '[dataContext.js][getCalibrationData()] Error fetching data: ',
          error.errorMessage
        );
        setCalibrationCampaign(null);
        setLiveCalibrationCampaign(false);
        return;
      }
      // 200 OK
      if (Configs.devEnvironment)
        console.log('[dataContext.js][getCalibrationData()] response: ', response);
      if (response.length > 0) {
        setCalibrationCampaign(response[0]);
        setLiveCalibrationCampaign(true);
        return response[0];
      } else {
        setCalibrationCampaign(null);
        setLiveCalibrationCampaign(false);
      }
    } catch (error) {
      console.error('[calibrationHome.js][getCalibrationData()] Error fetching data: ', error);
      setToasterMessage({ message: t('error.server-unreachable'), type: Configs.errorToaster });
      setIsCalibrationCampaignLoading(false);
      setCalibrationCampaign(null);
    }
  }

  // ------------------------------------------------
  // Pulse
  // ------------------------------------------------
  async function getPulseReportingData() {
    try {
      // Set loading status
      setIsPulseReportingDataLoading(true);

      const response = await apiCalls.postQuestionsReportingData(
        companyID,
        employeeID,
        token,
        correlationId,
        { companyID, employeeID, filters: {} }
      );
      if (Configs.devEnvironment)
        console.log('[dataContext.js][getPulseReportingData()] response: ', response);

      // Resolve loading status
      setIsPulseReportingDataLoading(false);

      // Review response status
      if (response === 500) {
        setPulseReportingData(null);
        setToasterMessage({ message: t('error.server-unreachable'), type: Configs.errorToaster });
      } else if (response === 404) {
        setPulseReportingData(null);
        setToasterMessage({ message: t('error.not-found'), type: Configs.errorToaster });
      } else if (response === 403) {
        setToasterMessage({ message: t('error.unauthorized'), type: Configs.errorToaster });
      } else if (response === 401) {
        setToasterMessage({ message: t('error.unauthenticated'), type: Configs.errorToaster });
        sessionStorage.clear();
      } else if (response === 400) {
        setToasterMessage({ message: t('error.bad-request'), type: Configs.errorToaster });
        setPulseReportingData(null);
      } else {
        if (Configs.devEnvironment)
          console.log('[dataContext.js][getPulseReportingData()] response: ', response);
        if (response?.length > 0) {
          setPulseReportingData(response);
        } else {
          setPulseReportingData(null);
        }
      }
    } catch (error) {
      console.error('[dataContext.js][getPulseReportingData()] Error fetching data: ', error);
      setToasterMessage({ message: t('error.server-unreachable'), type: Configs.errorToaster });
      setIsPulseReportingDataLoading(false);
    }
  }

  const getPulseKeyIndicators = useCallback(
    async (filtersInput = {}) => {
      if (!companyID || !employeeID || !token || isPulseKeyIndicatorsLoading) return;

      try {
        // Set loading status
        setIsPulseKeyIndicatorsLoading(true);

        let body = {
          companyID: companyID,
          employeeID: employeeID,
          filters: filtersInput,
        };

        const response = await apiCalls.getPulseKeyIndicators(
          companyID,
          employeeID,
          token,
          correlationId,
          body
        );
        if (Configs.devEnvironment)
          console.log('[dataContext.js][getPulseKeyIndicators()] response: ', response);

        // Resolve loading status
        setIsPulseKeyIndicatorsLoading(false);

        // Review response status
        if (response === 500) {
          setToasterMessage({ message: t('error.server-unreachable'), type: Configs.errorToaster });
        } else if (response === 404) {
          setToasterMessage({ message: t('error.not-found'), type: Configs.errorToaster });
        } else if (response === 403) {
          setToasterMessage({ message: t('error.unauthorized'), type: Configs.errorToaster });
        } else if (response === 401) {
          setToasterMessage({ message: t('error.unauthenticated'), type: Configs.errorToaster });
          sessionStorage.clear();
        } else if (response === 400) {
          setToasterMessage({ message: t('error.bad-request'), type: Configs.errorToaster });
        } else {
          if (Configs.devEnvironment)
            console.log('[dataContext.js][getPulseKeyIndicators()] response: ', response);
          if (Object.keys(response).length > 0) {
            setPulseKeyIndicators(response);
          } else {
            setPulseKeyIndicators([]);
          }
        }
      } catch (error) {
        console.error('[dataContext.js][getPulseKeyIndicators()] Error fetching data: ', error);
        setToasterMessage({ message: t('error.server-unreachable'), type: Configs.errorToaster });
        setIsPulseKeyIndicatorsLoading(false);
      }
    },
    [companyID, employeeID, token, correlationId, isPulseKeyIndicatorsLoading, pulseKeyIndicators]
  );

  const getPulseHomeGraphFrontendData = useCallback(async () => {
    if (
      !companyID ||
      !employeeID ||
      !token ||
      isPulseHomeGraphFrontendDataLoading ||
      pulseHomeGraphFrontendData
    )
      return;

    try {
      // Set loading status
      setIsPulseHomeGraphFrontendDataLoading(true);

      const response = await apiCalls.getPulseHomeGraphFrontendData(
        companyID,
        employeeID,
        token,
        correlationId
      );
      if (Configs.devEnvironment)
        console.log('[dataContext.js][getPulseHomeGraphFrontendData()] response: ', response);

      // Resolve loading status
      setIsPulseHomeGraphFrontendDataLoading(false);

      // Review response status
      if (response === 500) {
        setToasterMessage({ message: t('error.server-unreachable'), type: Configs.errorToaster });
      } else if (response === 404) {
        setToasterMessage({ message: t('error.not-found'), type: Configs.errorToaster });
      } else if (response === 403) {
        setToasterMessage({ message: t('error.unauthorized'), type: Configs.errorToaster });
      } else if (response === 401) {
        setToasterMessage({ message: t('error.unauthenticated'), type: Configs.errorToaster });
        sessionStorage.clear();
      } else if (response === 400) {
        setToasterMessage({ message: t('error.bad-request'), type: Configs.errorToaster });
      } else {
        if (Configs.devEnvironment)
          console.log('[dataContext.js][getPulseHomeGraphFrontendData()] response: ', response);
        if (response.length > 0) {
          setPulseHomeGraphFrontendData(response);
        } else {
          setPulseHomeGraphFrontendData([]);
        }
      }
    } catch (error) {
      console.error(
        '[dataContext.js][getPulseHomeGraphFrontendData()] Error fetching data: ',
        error
      );
      setToasterMessage({ message: t('error.server-unreachable'), type: Configs.errorToaster });
      setIsPulseHomeGraphFrontendDataLoading(false);
    }
  }, [
    companyID,
    employeeID,
    token,
    correlationId,
    isPulseHomeGraphFrontendDataLoading,
    pulseHomeGraphFrontendData,
  ]);

  const getAllPulseQuestions = useCallback(async () => {
    if (!companyID || !employeeID || !token || isAllPulseQuestionsLoading || allPulseQuestions)
      return;

    try {
      setIsAllPulseQuestionsLoading(true);

      const response = await apiCalls.getAllPulseQuestions(
        companyID,
        employeeID,
        token,
        correlationId
      );

      if (Configs.devEnvironment)
        console.log('[dataContext.js][getAllPulseQuestions()] response: ', response);

      setIsAllPulseQuestionsLoading(false);

      if (response === 500) {
        setToasterMessage({ message: t('error.server-unreachable'), type: Configs.errorToaster });
      } else if (response === 404) {
        setToasterMessage({ message: t('error.not-found'), type: Configs.errorToaster });
      } else if (response === 403) {
        setToasterMessage({ message: t('error.unauthorized'), type: Configs.errorToaster });
      } else if (response === 401) {
        setToasterMessage({ message: t('error.unauthenticated'), type: Configs.errorToaster });
        sessionStorage.clear();
      } else if (response === 400) {
        setToasterMessage({ message: t('error.bad-request'), type: Configs.errorToaster });
      } else {
        if (Configs.devEnvironment)
          console.log('[dataContext.js][getAllPulseQuestions()] response: ', response);
        if (response.length > 0) {
          setAllPulseQuestions(response);
          sessionStorage.setItem(
            'allPulseQuestions',
            CryptoJS.AES.encrypt(JSON.stringify(response), Configs.privateKey).toString()
          );
        }
      }
    } catch (error) {
      console.error('[dataContext.js][getAllPulseQuestions()] Error fetching data: ', error);
      setToasterMessage({ message: t('error.server-unreachable'), type: Configs.errorToaster });
      setAllPulseQuestions([]);
      setIsAllPulseQuestionsLoading(false);
    }
  }, [companyID, employeeID, token, correlationId, isAllPulseQuestionsLoading, allPulseQuestions]);

  // ------------------------------------------------
  // ENGAGEMENT - 1on1s - MEETINGS
  // ------------------------------------------------
  // 1:1 space is loaded via TanStack `useGetOneOnOneSpace` (Career home + OneOnOneHistoryList).

  // ------------------------------------------------
  // Learning
  // ------------------------------------------------
  const getLearningCoursesCatalogue = useCallback(async () => {
    if (
      !companyID ||
      !employeeID ||
      !token ||
      isLearningCoursesCatalogueLoading ||
      learningCoursesCatalogue
    )
      return;

    try {
      // Set loading status
      setIsLearningCoursesCatalogueLoading(true);

      const response = await apiCalls.getLearningCoursesCatalogue(
        companyID,
        employeeID,
        token,
        correlationId
      );
      if (Configs.devEnvironment)
        console.log('[dataContext.js][getLearningCoursesCatalogue()] response: ', response);

      // Resolve loading status
      setIsLearningCoursesCatalogueLoading(false);

      // Error handling
      const error = handleApiError(response, t);

      if (error.isError) {
        setToasterMessage({ message: error.errorMessage, type: Configs.errorToaster });
        console.error(
          '[dataContext.js][getLearningCoursesCatalogue()] Error fetching data: ',
          error.errorMessage
        );
        setLearningCoursesCatalogue([]);
        return;
      }

      if (Configs.devEnvironment)
        console.log('[dataContext.js][getLearningCoursesCatalogue()] response: ', response);

      if (response && response.length > 0) {
        setLearningCoursesCatalogue(response);
        return response;
      }

      setLearningCoursesCatalogue([]);
      return;
    } catch (error) {
      console.error('[dataContext.js][getLearningCoursesCatalogue()] Error fetching data: ', error);
      setToasterMessage({ message: t('error.server-unreachable'), type: Configs.errorToaster });
      setIsLearningCoursesCatalogueLoading(false);
    }
  }, [
    companyID,
    employeeID,
    token,
    correlationId,
    isLearningCoursesCatalogueLoading,
    learningCoursesCatalogue,
  ]);

  // ------------------------------------------------
  // ------------------------------------------------
  // SUPER ADMIN
  // ------------------------------------------------
  async function getCCOaaSReport() {
    try {
      setIsCCOaaSReportLoading(true);

      const response = await apiCalls.getCCOaaSReport(companyID, employeeID, token, correlationId);

      setIsCCOaaSReportLoading(false);

      if (response === 500) {
        setToasterMessage({ message: t('error.server-unreachable'), type: Configs.errorToaster });
      } else if (response === 404) {
        setToasterMessage({ message: t('error.not-found'), type: Configs.errorToaster });
      } else if (response === 403) {
        setToasterMessage({ message: t('error.unauthorized'), type: Configs.errorToaster });
        sessionStorage.clear();
      } else if (response === 401) {
        setToasterMessage({ message: t('error.unauthenticated'), type: Configs.errorToaster });
        sessionStorage.clear();
      } else if (response === 400) {
        setToasterMessage({ message: t('error.bad-request'), type: Configs.errorToaster });
      } else {
        if (Configs.devEnvironment)
          console.log('[dataContext.js][getCCOaaSReport()] response: ', response);
        if (response.length > 0) setCCOaaSReport(response);
      }
    } catch (error) {
      console.error('[dataContext.js][getCCOaaSReport()] Error fetching data: ', error);
      setToasterMessage({ message: t('error.server-unreachable'), type: Configs.errorToaster });
      setIsCCOaaSReportLoading(false);
    }
  }

  async function getCCOaaSCompanyDetailsAIAnalysis(selectedCompanyID = 1) {
    try {
      setIsCCOaaSAICompanyDetailsAnalysisLoading(true);

      const response = await apiCalls.getCCOaaSCompanyDetailsAIAnalysis(
        companyID,
        employeeID,
        token,
        selectedCompanyID,
        correlationId
      );

      setIsCCOaaSAICompanyDetailsAnalysisLoading(false);

      if (response === 500) {
        setToasterMessage({ message: t('error.server-unreachable'), type: Configs.errorToaster });
      } else if (response === 404) {
        setToasterMessage({ message: t('error.not-found'), type: Configs.errorToaster });
      } else if (response === 403) {
        setToasterMessage({ message: t('error.unauthorized'), type: Configs.errorToaster });
        sessionStorage.clear();
      } else if (response === 401) {
        setToasterMessage({ message: t('error.unauthenticated'), type: Configs.errorToaster });
        sessionStorage.clear();
      } else if (response === 400) {
        setToasterMessage({ message: t('error.bad-request'), type: Configs.errorToaster });
      } else {
        if (Configs.devEnvironment)
          console.log('[dataContext.js][getCCOaaSCompanyDetailsAIAnalysis()] response: ', response);
        if (response.length > 0) setCCOaaSCompanyDetailsAIAnalysis(JSON.parse(response));
      }
    } catch (error) {
      console.error(
        '[dataContext.js][getCCOaaSCompanyDetailsAIAnalysis()] Error fetching data: ',
        error
      );
      setToasterMessage({ message: t('error.server-unreachable'), type: Configs.errorToaster });
      setIsCCOaaSAICompanyDetailsAnalysisLoading(false);
    }
  }

  // LOGOUT
  async function dataLogout() {
    // Set all variables to null
    setProfile(null);
    setCompanyPreferences(null);
    setCompanyBackgroundImageURL(null);
    setTeamDirects(null);
    setTeamOrg(null);
    setSelectedProfile(null);
    setPendingToDos(null);
    setKeyKPIs(null);
    setPersonalPerformanceData(null);
    setLivePerformanceCampaign(null);
    setPerformanceReviewCampaigns(null);
    setPerformanceReviewHistory(null);
    setPerformanceFeedbackProviders(null);
    setCalibrationCampaign(null);
    setLiveCalibrationCampaign(null);
    if (companyID != null && employeeID != null) {
      await queryKeysInvalidate.allRecognitionDataForCompanyPages(companyID, employeeID);
      await queryKeysInvalidate.allRecognitionLeaderboardsForCompanyPages(companyID, employeeID);
      await queryKeysInvalidate.getRewardsDataForCompany(companyID, employeeID);
      await queryKeysInvalidate.getTeamHolidayCalendar(companyID, employeeID);
      await queryKeysInvalidate.allCompanyEmployeesPagesForActor(companyID, employeeID);
    }
    try {
      sessionStorage.removeItem('companyOrg');
      sessionStorage.removeItem('companyMembers');
    } catch (e) {
      console.error('[dataContext.jsx][dataLogout()] error clearing company org sessionStorage', e);
    }
    setPulseReportingData(null);
    setPulseKeyIndicators(null);
    setPulseHomeGraphFrontendData(null);
    setAllPulseQuestions(null);
    setJobLevellingGuidelinesData(null);
    setCCOaaSReport(null);
    setCCOaaSCompanyDetailsAIAnalysis(null);
    setToasterMessage(null);
    setLogoPublicSmallURL(null);
    setLogoPublicLargeURL(null);
    setToasterMessage(null);
  }

  // ------------------------------------------------
  // USE EFFECTS
  // ------------------------------------------------
  useEffect(() => {
    // Do not run if any of the required data is missing
    if (!(companyID && employeeID && token && securityLevel && correlationId)) return;

    const fetchData = async () => {
      try {
        // Company logo
        if (companyPreferences?.personalization?.logoLarge) getPublicLogoLargeURL();
        if (companyPreferences?.personalization?.logoSmall) getPublicLogoSmallURL();
        if (companyPreferences?.personalization?.backgroundImageURL) getPublicBackgroundImageURL();

        // Team and company data
        if (
          teamDirects === null &&
          securityLevel?.includes(Configs.securityLevelManager) &&
          !isDirectsLoading
        )
          getMyDirectTeam();
        if (
          teamOrg === null &&
          securityLevel?.includes(Configs.securityLevelManager) &&
          !isOrgLoading
        )
          getMyTeamOrg();

        // ENGAGEMENT
        // Pulse
        if (companyPreferences?.activeModules['Pulse']) {
          if (
            securityLevel?.includes(Configs.securityLevelManager) ||
            securityLevel?.includes(Configs.securityLevelHRBP) ||
            securityLevel?.includes(Configs.securityLevelHR) ||
            securityLevel?.includes(Configs.securityLevelExec)
          ) {
            // Pulse home loads KPIs with timeframe + filters via `pulseHome.jsx` (`buildPulseKeyIndicatorsPayload`).
            // Do not prefetch here with an empty body — that skips `timeframe` and requested all-time data.
            // if (pulseHomeGraphFrontendData === null && !isPulseHomeGraphFrontendDataLoading) getPulseHomeGraphFrontendData();
            if (allPulseQuestions === null && !isAllPulseQuestionsLoading) getAllPulseQuestions();
          }

          if (
            securityLevel?.includes(Configs.securityLevelHRBP) ||
            securityLevel?.includes(Configs.securityLevelHR) ||
            securityLevel?.includes(Configs.securityLevelExec)
          ) {
            if (pulseReportingData === null && !isPulseReportingDataLoading)
              getPulseReportingData();
          }
        }
        // PERFORMANCE
        // Calibration
        if (companyPreferences?.activeModules['Calibration']) {
          if (
            securityLevel?.includes(Configs.securityLevelManager) ||
            securityLevel?.includes(Configs.securityLevelHR) ||
            securityLevel?.includes(Configs.securityLevelExec) ||
            securityLevel?.includes(Configs.securityLevelAdmin)
          ) {
            if (calibrationCampaign === null && !isCalibrationCampaignLoading) getCalibrationData();
          }
        }

        // Career guidelines list is loaded via `useGetCareerJobGuidelines` (TanStack).

        // Days Off
        if (companyPreferences?.activeModules['Time-off']) {
          // Employee
          if (selfHolidays === null && !isSelfHolidaysLoading) getSelfHolidays();

          // Manager, HR, Exec
          if (
            securityLevel?.includes(Configs.securityLevelManager) ||
            securityLevel?.includes(Configs.securityLevelHR) ||
            securityLevel?.includes(Configs.securityLevelExec)
          ) {
            if (holidaysTeamPendingRequests === null && !isHolidaysTeamPendingRequestsLoading)
              getHolidaysTeamPendingRequests();
          }
        }
      } catch (error) {
        console.error('[dataContext.js][useEffect()] Error fetching data: ', error);
      }
    };

    if (companyID && employeeID && token && securityLevel) fetchData();

    try {
      // create company css variable
      createCompanyColorCssVariable(profile?.companyPreferences?.personalization?.colorHex);
    } catch (error) {
      console.error('[dataContext.js][getProfile()] Error creating company css variable: ', error);
    }

    if (Configs.devEnvironment)
      console.log(
        '[dataContext.js][useEffect()] context: ',
        companyID,
        employeeID,
        securityLevel,
        profile,
        selectedProfile
      );
  }, [
    employeeID,
    companyID,
    token,
    securityLevel,
    correlationId,
    getPublicLogoLargeURL,
    getPublicLogoSmallURL,
    getPublicBackgroundImageURL, // UI Personalization
  ]);

  // ------------------------------------------------
  useEffect(() => {
    // Do not run if any of the required data is missing
    if (!(companyID && employeeID && token && securityLevel && correlationId)) return;

    const fetchData = async () => {
      try {
        // User profile
        if (profile === null) getProfile();

        // Context switching — fetch full profile once for a roster/org row.
        // Skip when selectedProfile already looks like getProfile payload (has employeeInfo),
        // otherwise employeeID nesting re-triggers getProfile and Loader remount loops.
        {
          const selectedId =
            selectedProfile?.employeeID ?? selectedProfile?.employeeInfo?.employeeID;
          const alreadyHydrated = Boolean(selectedProfile?.employeeInfo);
          if (
            selectedProfile &&
            selectedId != null &&
            String(selectedId) !== String(employeeID) &&
            !alreadyHydrated
          ) {
            getProfile(selectedId, companyID, token, null);
          }
        }

        // PERFORMANCE
        // Performance Review
        if (companyPreferences?.activeModules['Performance-Review']) {
          getLivePerformanceCampaigns();
          getPerformanceReviewHistory();
        }

        // 1:1 space timeline: TanStack `useGetOneOnOneSpace` (Career home + OneOnOneHistoryList).
      } catch (error) {
        console.error('[dataContext.js][useEffect()] Error fetching data: ', error);
      }
    };

    if (companyID && employeeID && token && correlationId) fetchData();

    if (Configs.devEnvironment)
      console.log(
        '[dataContext.js][useEffect()] context: ',
        companyID,
        employeeID,
        securityLevel,
        profile,
        selectedProfile
      );
  }, [employeeID, companyID, token, securityLevel, selectedProfile]);

  // ------------------------------------------------
  // SUPER ADMIN useEffect
  useEffect(() => {
    // Do not run if any of the required data is missing
    if (!(companyID && employeeID && token && securityLevel && correlationId)) return;

    const fetchData = async () => {
      // SUPER ADMIN
      // if (CCOaaSReport === null) getCCOaaSReport();
      if (selectedCompany !== null) {
        if (CCOaaScompanyDetailsAIAnalysis === null && !isCCOaaSAICompanyDetailsAnalysisLoading)
          getCCOaaSCompanyDetailsAIAnalysis(selectedCompany);
      }
    };

    if (
      companyPreferences?.activeModules['Admin'] &&
      securityLevel?.includes(Configs.securityLevelSuperAdmin)
    ) {
      fetchData();
    }
  }, [employeeID, companyID, token, securityLevel, selectedCompany]);

  // ------------------------------------------------
  // Return
  // ------------------------------------------------
  return (
    <DataContext.Provider
      value={{
        // Toaster
        toasterMessage,
        setToasterMessage,
        closeToaster,
        // Enhanced toaster
        toasterMessages,
        addToast,
        removeToast,
        clearAllToasts,
        // Navbar
        navBarCollapsed,
        setCollapseNavBar,

        // Feedback Popup
        showFeedbackPopup,
        setShowFeedbackPopup,

        // Company logo
        logoPublicLargeURL,
        setLogoPublicLargeURL,
        getPublicLogoLargeURL,
        logoPublicSmallURL,
        setLogoPublicSmallURL,
        getPublicLogoSmallURL,

        // Profile
        profile,
        setProfile,
        getProfile,
        isProfileLoading,
        errorLoadingProfile,

        // Company preferences,
        companyPreferences,
        setCompanyPreferences,

        // Company Background Image URL
        companyBackgroundImageURL,
        setCompanyBackgroundImageURL,

        // Team
        teamDirects,
        setTeamDirects,
        getMyDirectTeam,
        isDirectsLoading,
        teamOrg,
        setTeamOrg,
        getMyTeamOrg,
        isOrgLoading,

        // Context switching
        selectedProfile,
        setSelectedProfile,
        isSelectedProfileLoading,
        setIsSelectedProfileLoading,

        // Home
        // Pending to-dos
        pendingToDos,
        setPendingToDos,
        getPendingToDos,
        isPendingToDosLoading,
        setIsPendingToDosLoading,

        // Key KPIs
        keyKPIs,
        setKeyKPIs,
        getKeyKPIs,
        isKeyKPIsLoading,
        setIsKeyKPIsLoading,

        // PERFORMANCE
        // Performance Review - Data Personal
        personalPerformanceData,
        setPersonalPerformanceData,
        getPersonalPerformanceData,
        isPersonalPerformanceLoading,
        setIsPersonalPerformanceLoading,

        // Performance review - Live Campaigns
        getLivePerformanceCampaigns,
        isPerformanceLiveCampaignsLoading,
        setIsPerformanceLiveCampaignsLoading,
        livePerformanceCampaign,
        setLivePerformanceCampaign,
        performanceReviewCampaigns,
        setPerformanceReviewCampaigns,
        getPerformanceReviewHistory,
        isPerformanceReviewHistoryLoading,
        setIsPerformanceReviewHistoryLoading,
        performanceReviewHistory,
        setPerformanceReviewHistory,
        performanceFeedbackProviders,
        setPerformanceFeedbackProviders,
        getPerformanceFeedbackProviders,

        // Calibration
        calibrationCampaign,
        setCalibrationCampaign,
        getCalibrationData,
        isCalibrationCampaignLoading,
        setIsCalibrationCampaignLoading,
        liveCalibrationCampaign,
        setLiveCalibrationCampaign,

        // Learning
        learningCoursesCatalogue,
        setLearningCoursesCatalogue,
        getLearningCoursesCatalogue,
        isLearningCoursesCatalogueLoading,
        setIsLearningCoursesCatalogueLoading,

        // Pulse
        pulseReportingData,
        setPulseReportingData,
        getPulseReportingData,
        isPulseReportingDataLoading,
        setIsPulseReportingDataLoading,
        pulseKeyIndicators,
        setPulseKeyIndicators,
        getPulseKeyIndicators,
        isPulseKeyIndicatorsLoading,
        setIsPulseKeyIndicatorsLoading,
        pulseHomeGraphFrontendData,
        setPulseHomeGraphFrontendData,
        getPulseHomeGraphFrontendData,
        isPulseHomeGraphFrontendDataLoading,
        setIsPulseHomeGraphFrontendDataLoading,
        allPulseQuestions,
        setAllPulseQuestions,
        getAllPulseQuestions,
        isAllPulseQuestionsLoading,
        setIsAllPulseQuestionsLoading,

        // Time off
        holidaysTeamPendingRequests,
        setHolidaysTeamPendingRequests,
        getHolidaysTeamPendingRequests,
        isHolidaysTeamPendingRequestsLoading,
        setIsHolidaysTeamPendingRequestsLoading,
        selfHolidays,
        setSelfHolidays,
        getSelfHolidays,
        isSelfHolidaysLoading,
        setIsSelfHolidaysLoading,

        // SUPER ADMIN
        selectedCompany,
        setSelectedCompany,
        CCOaaSReport,
        setCCOaaSReport,
        getCCOaaSReport,
        isCCOaaSReportLoading,
        setIsCCOaaSReportLoading,
        CCOaaScompanyDetailsAIAnalysis,
        setCCOaaSCompanyDetailsAIAnalysis,
        getCCOaaSCompanyDetailsAIAnalysis,
        isCCOaaSAICompanyDetailsAnalysisLoading,
        setIsCCOaaSAICompanyDetailsAnalysisLoading,

        // LOGOUT
        dataLogout,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

/**
 * @returns {import('./dataContext').DataContextValues}
 */
export const useData = () => useContext(DataContext);
